/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { itemQty } from "@/utils/itemValue";

/**
 * RecordSaleModal — logs a sale against a single item.
 *
 * Props:
 *   open, onOpenChange — dialog control
 *   item — the item being sold (must expose id, item_name, item_data.qty, cost_price_ex_gst)
 *   onSaleRecorded(sale) — parent callback after the API returns 200
 *
 * Validation mirrors the backend contract in server.py `record_sale`:
 *   quantity_sold ≤ item.qty, ex+inc prices required, sale_date required.
 */
export default function RecordSaleModal({ open, onOpenChange, item, onSaleRecorded }) {
  const currentQty = itemQty(item);
  const [qty, setQty] = useState("1");
  const [priceEx, setPriceEx] = useState("");
  const [priceInc, setPriceInc] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [buyer, setBuyer] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset every time the modal opens so an old draft never bleeds
  // across items.
  useEffect(() => {
    if (open) {
      setQty("1");
      setPriceEx("");
      setPriceInc("");
      setSaleDate(new Date().toISOString().slice(0, 10));
      setBuyer("");
      setNotes("");
    }
  }, [open, item?.id]);

  const errors = useMemo(() => {
    const errs = {};
    const q = Number(qty);
    if (!qty || Number.isNaN(q)) errs.qty = "Quantity is required";
    else if (q <= 0) errs.qty = "Quantity must be greater than 0";
    else if (!Number.isInteger(q)) errs.qty = "Quantity must be a whole number";
    else if (q > currentQty) errs.qty = `Only ${currentQty} available`;

    const pEx = Number(priceEx);
    if (priceEx === "" || Number.isNaN(pEx)) errs.priceEx = "Ex-GST price required";
    else if (pEx < 0) errs.priceEx = "Ex-GST price must be ≥ 0";

    const pInc = Number(priceInc);
    if (priceInc === "" || Number.isNaN(pInc)) errs.priceInc = "Inc-GST price required";
    else if (pInc < 0) errs.priceInc = "Inc-GST price must be ≥ 0";

    if (!saleDate) errs.saleDate = "Sale date required";
    return errs;
  }, [qty, priceEx, priceInc, saleDate, currentQty]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  // Dynamic tax multiplier — mirrors utils/itemValue._itemTaxRate.
  // Prefers the item's stored `tax_percent` (historical snapshot at
  // purchase); falls back to the global `cdi.settings.taxPercent`. No
  // hard-coded 1.15 anywhere.
  const taxMultiplier = useMemo(() => {
    const readGlobal = () => {
      try {
        const raw = typeof window !== "undefined"
          ? window.localStorage.getItem("cdi.settings.taxPercent")
          : null;
        const n = Number(raw);
        return Number.isFinite(n) && n >= 0 ? n : 0;
      } catch { return 0; }
    };
    const stored = item?.tax_percent;
    const hasStored = stored !== null && stored !== undefined && stored !== "";
    const n = hasStored ? Number(stored) : NaN;
    const pct = Number.isFinite(n) && n >= 0 ? n : readGlobal();
    return 1 + (pct / 100);
  }, [item?.tax_percent, open]);

  // Auto-derive the missing GST-side while the other is being typed —
  // matches how ItemEntry handles Buy Price. Only fires when one side is
  // blank so the user can still enter both explicitly.
  const onPriceExBlur = () => {
    const ex = Number(priceEx);
    if (ex && !Number(priceInc)) setPriceInc((ex * taxMultiplier).toFixed(2));
  };
  const onPriceIncBlur = () => {
    const inc = Number(priceInc);
    if (inc && !Number(priceEx)) setPriceEx((inc / taxMultiplier).toFixed(2));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await apiClient.post("/sales", {
        item_id: item.id,
        quantity_sold: Number(qty),
        sale_price_ex_gst: Number(priceEx),
        sale_price_inc_gst: Number(priceInc),
        sale_date: saleDate,
        buyer: buyer.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      // Await the parent refetch so the underlying item page shows the
      // updated quantity, case/loose breakdown, valuation, and sales
      // history the moment the modal closes. Without this await the
      // dialog closes over stale content until the background fetch
      // resolves. Any refresh error propagates into the outer catch so
      // the user sees an actionable toast instead of a silent stale
      // page.
      await onSaleRecorded?.(res.data);
      toast.success("Sale recorded");
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Surface field-level errors when the backend returns a strict
      // 422 payload; fall through to the plain string on plain 400s.
      let msg;
      if (detail && typeof detail === "object" && detail.errors) {
        msg = Object.values(detail.errors)[0] || detail.message;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg || "Failed to record sale");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-sale-modal">
        <DialogHeader>
          <DialogTitle>Record Sale</DialogTitle>
          <p className="text-xs vp-muted mt-1">
            {item?.item_name || item?.name || "Item"} · {currentQty} available
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <Label htmlFor="rs-qty">Quantity sold</Label>
            <Input
              id="rs-qty"
              type="number"
              min="1"
              max={currentQty}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="rs-qty"
            />
            {errors.qty && <p className="text-xs text-red-500 mt-1">{errors.qty}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rs-price-ex">Sale price ex {item?.tax_name || "Tax"} — {item?.currency_code || "NZD"}</Label>
              <Input
                id="rs-price-ex"
                type="number"
                step="0.01"
                value={priceEx}
                onChange={(e) => setPriceEx(e.target.value)}
                onBlur={onPriceExBlur}
                data-testid="rs-price-ex"
              />
              {errors.priceEx && <p className="text-xs text-red-500 mt-1">{errors.priceEx}</p>}
            </div>
            <div>
              <Label htmlFor="rs-price-inc">Sale price inc {item?.tax_name || "Tax"} — {item?.currency_code || "NZD"}</Label>
              <Input
                id="rs-price-inc"
                type="number"
                step="0.01"
                value={priceInc}
                onChange={(e) => setPriceInc(e.target.value)}
                onBlur={onPriceIncBlur}
                data-testid="rs-price-inc"
              />
              {errors.priceInc && <p className="text-xs text-red-500 mt-1">{errors.priceInc}</p>}
            </div>
          </div>

          <div>
            <Label htmlFor="rs-date">Sale date</Label>
            <Input
              id="rs-date"
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              data-testid="rs-date"
            />
            {errors.saleDate && <p className="text-xs text-red-500 mt-1">{errors.saleDate}</p>}
          </div>

          <div>
            <Label htmlFor="rs-buyer">Buyer <span className="vp-muted">(optional)</span></Label>
            <Input
              id="rs-buyer"
              value={buyer}
              onChange={(e) => setBuyer(e.target.value)}
              data-testid="rs-buyer"
            />
          </div>

          <div>
            <Label htmlFor="rs-notes">Notes <span className="vp-muted">(optional)</span></Label>
            <Textarea
              id="rs-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="rs-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rs-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="rs-submit">
            {submitting ? "Recording…" : "Record Sale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
